import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Mail, Clock, CheckCircle, XCircle, 
  Copy, Plus, RefreshCw, Ban, UserCheck
} from 'lucide-react';
import inviteService from '../services/inviteService';
import { useBusinessContext } from '../contexts/BusinessContext';
import Loading from '../components/Loading';

const TeamManagement = () => {
  const navigate = useNavigate();
  const { user, isOwner } = useBusinessContext();
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, accepted, expired
  const [copySuccess, setCopySuccess] = useState(null);

  // Redirect if not owner
  useEffect(() => {
    if (!isOwner) {
      navigate('/dashboard');
    }
  }, [isOwner, navigate]);

  useEffect(() => {
    loadInvites();
  }, []);

  const loadInvites = async () => {
    try {
      setLoading(true);
      const data = await inviteService.getAllInvites();
      setInvites(data.invites || []);
    } catch (error) {
      console.error('Error loading invites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async (inviteLink, inviteId) => {
    try {
      await inviteService.copyInviteLink(inviteLink);
      setCopySuccess(inviteId);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
      alert('Failed to copy link');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      await inviteService.cancelInvite(inviteId);
      await loadInvites();
    } catch (error) {
      console.error('Error cancelling invite:', error);
      alert('Failed to cancel invitation');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await inviteService.resendInvite(inviteId);
      await loadInvites();
      alert('Invitation resent successfully!');
    } catch (error) {
      console.error('Error resending invite:', error);
      alert('Failed to resend invitation');
    }
  };

  // Filter invites
  const filteredInvites = invites.filter(invite => {
    if (filter === 'all') return true;
    if (filter === 'pending') return invite.status === 'Pending' && new Date(invite.expiresAt) > new Date();
    if (filter === 'accepted') return invite.status === 'Accepted';
    if (filter === 'expired') return invite.status === 'Pending' && new Date(invite.expiresAt) <= new Date();
    return true;
  });

  // Calculate stats
  const stats = {
    total: invites.length,
    pending: invites.filter(i => i.status === 'Pending' && new Date(i.expiresAt) > new Date()).length,
    accepted: invites.filter(i => i.status === 'Accepted').length,
    expired: invites.filter(i => i.status === 'Pending' && new Date(i.expiresAt) <= new Date()).length,
  };

  const getStatusBadge = (invite) => {
    const isExpired = new Date(invite.expiresAt) <= new Date();
    
    if (invite.status === 'Accepted') {
      return <span className="badge bg-green-100 text-green-700">✓ Accepted</span>;
    }
    if (invite.status === 'Cancelled') {
      return <span className="badge bg-gray-100 text-gray-700">Cancelled</span>;
    }
    if (isExpired) {
      return <span className="badge bg-red-100 text-red-700">Expired</span>;
    }
    return <span className="badge bg-yellow-100 text-yellow-700">⏳ Pending</span>;
  };

  const getExpirationText = (expiresAt) => {
    const expiration = inviteService.getExpirationStatus(expiresAt);
    return (
      <span className={`text-sm text-${expiration.color}-600`}>
        {expiration.text}
      </span>
    );
  };

  if (loading) return <Loading fullScreen />;

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
            <p className="text-gray-600 mt-1">Manage employee invitations and team access</p>
          </div>
          <button
            onClick={() => navigate('/team/invite')}
            className="btn btn-primary flex items-center gap-2 justify-center"
          >
            <Plus size={20} />
            Invite Employee
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Team Members</p>
              <p className="text-3xl font-bold text-blue-900">{stats.accepted + 1}</p>
              <p className="text-xs text-blue-600 mt-1">Including you</p>
            </div>
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <Users className="text-white" size={24} />
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">Pending Invites</p>
              <p className="text-3xl font-bold text-yellow-900">{stats.pending}</p>
              <p className="text-xs text-yellow-600 mt-1">Awaiting response</p>
            </div>
            <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
              <Clock className="text-white" size={24} />
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Accepted</p>
              <p className="text-3xl font-bold text-green-900">{stats.accepted}</p>
              <p className="text-xs text-green-600 mt-1">Active employees</p>
            </div>
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-white" size={24} />
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Expired</p>
              <p className="text-3xl font-bold text-red-900">{stats.expired}</p>
              <p className="text-xs text-red-600 mt-1">Need resending</p>
            </div>
            <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
              <XCircle className="text-white" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
        {[
          { key: 'all', label: 'All Invites', count: stats.total },
          { key: 'pending', label: 'Pending', count: stats.pending },
          { key: 'accepted', label: 'Accepted', count: stats.accepted },
          { key: 'expired', label: 'Expired', count: stats.expired },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === tab.key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Current User Card (Owner) */}
      <div className="card mb-4 border-2 border-green-200 bg-gradient-to-r from-green-50 to-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <UserCheck className="text-green-600" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">
                {user?.firstName} {user?.lastName}
              </h3>
              <span className="badge bg-green-100 text-green-700 font-semibold">Owner</span>
            </div>
            <p className="text-sm text-gray-600">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Invitations List */}
      {filteredInvites.length === 0 ? (
        <div className="card text-center py-12">
          <Mail className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-500 mb-4">
            {filter === 'all' 
              ? 'No employee invitations yet' 
              : `No ${filter} invitations`}
          </p>
          <button
            onClick={() => navigate('/team/invite')}
            className="btn btn-primary mx-auto"
          >
            <Plus size={20} className="mr-2" />
            Send Your First Invite
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInvites.map((invite) => (
            <div key={invite.id} className="card hover:shadow-md transition-shadow">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">
                        {invite.firstName} {invite.lastName}
                      </h3>
                      <p className="text-gray-600">{invite.email}</p>
                    </div>
                    <div className="lg:hidden">
                      {getStatusBadge(invite)}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-3">
                    <div>
                      <span className="font-medium">Invited by:</span> {invite.invitedByName}
                    </div>
                    <div>
                      <span className="font-medium">Created:</span>{' '}
                      {new Date(invite.createdAt).toLocaleDateString()}
                    </div>
                    {invite.status === 'Pending' && (
                      <div>
                        <span className="font-medium">Expires:</span>{' '}
                        {getExpirationText(invite.expiresAt)}
                      </div>
                    )}
                    {invite.acceptedAt && (
                      <div className="text-green-600">
                        <span className="font-medium">Accepted:</span>{' '}
                        {new Date(invite.acceptedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {invite.message && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700 italic">"{invite.message}"</p>
                    </div>
                  )}
                </div>

                <div className="flex lg:flex-col items-center gap-2">
                  <div className="hidden lg:block mb-2">
                    {getStatusBadge(invite)}
                  </div>

                  {invite.status === 'Pending' && new Date(invite.expiresAt) > new Date() && (
                    <>
                      <button
                        onClick={() => handleCopyLink(invite.inviteLink, invite.id)}
                        className="btn btn-secondary text-sm py-1.5 flex items-center gap-1"
                        title="Copy invite link"
                      >
                        {copySuccess === invite.id ? (
                          <>
                            <CheckCircle size={16} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={16} />
                            Copy Link
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleResendInvite(invite.id)}
                        className="btn btn-secondary text-sm py-1.5 flex items-center gap-1"
                        title="Resend invitation"
                      >
                        <RefreshCw size={16} />
                        Resend
                      </button>
                      <button
                        onClick={() => handleCancelInvite(invite.id)}
                        className="btn btn-danger text-sm py-1.5 flex items-center gap-1"
                        title="Cancel invitation"
                      >
                        <Ban size={16} />
                        Cancel
                      </button>
                    </>
                  )}

                  {invite.status === 'Pending' && new Date(invite.expiresAt) <= new Date() && (
                    <button
                      onClick={() => handleResendInvite(invite.id)}
                      className="btn btn-primary text-sm py-1.5 flex items-center gap-1"
                    >
                      <RefreshCw size={16} />
                      Resend
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamManagement;